/** Deck helpers shared by server (AI opponents, validation) and tests. */
import { DECK_RULES, type Card, type DeckInput } from '@gooflord/shared';
import { nextRandom, shuffleWith } from './rng.js';

export interface DeckCheck { ok: boolean; errors: string[] }

/** Copies of a card one deck may hold: the deck rules' limit, or a card's own lower one. */
export const copiesAllowed = (c: Card) => Math.min(DECK_RULES.maxCopies, c.maxCopies ?? DECK_RULES.maxCopies);

/** Is this a legal deck given what the player owns? `owned` maps slug → quantity. */
export function checkDeck(fighters: string[], items: string[], gooflord: string, owned: Record<string, number>, cat: Record<string, Card>): DeckCheck {
  const errors: string[] = [];
  const count = (arr: string[], s: string) => arr.filter((x) => x === s).length;
  if (fighters.length < DECK_RULES.fightersMin || fighters.length > DECK_RULES.fightersMax) errors.push(`Fighters: ${DECK_RULES.fightersMin}–${DECK_RULES.fightersMax} cards (you have ${fighters.length})`);
  if (items.length < DECK_RULES.itemsMin || items.length > DECK_RULES.itemsMax) errors.push(`Items: ${DECK_RULES.itemsMin}–${DECK_RULES.itemsMax} cards (you have ${items.length})`);
  const corrupted = fighters.filter((s) => cat[s]?.type === 'corrupted').length;
  if (corrupted > DECK_RULES.corruptedMax) errors.push(`Only ${DECK_RULES.corruptedMax} Corrupted cards per deck (you have ${corrupted})`);
  for (const s of new Set([...fighters, ...items])) {
    const c = cat[s];
    if (!c) { errors.push(`Unknown card ${s}`); continue; }
    const n = count(fighters, s) + count(items, s);
    const max = Math.min(copiesAllowed(c), owned[s] ?? 0);
    if (n > max) errors.push(`You can only run ${max} of ${c.name}`);
    if (fighters.includes(s) && c.type !== 'fighter' && c.type !== 'corrupted') errors.push(`${c.name} is not a fighter`);
    if (items.includes(s) && c.type !== 'item') errors.push(`${c.name} is not an item`);
  }
  if (!cat[gooflord] || cat[gooflord].type !== 'gooflord') errors.push('Pick a Gooflord leader');
  else if (!(owned[gooflord] ?? 0)) errors.push(`You don't own ${cat[gooflord].name}`);
  return { ok: !errors.length, errors };
}

/** Auto-build a legal deck from a collection (new players, or when a saved deck has gone stale). */
export function autoDeck(owned: Record<string, number>, cat: Record<string, Card>, prefGooflord?: string): { gooflord: string; fighters: string[]; items: string[] } {
  const fighters: string[] = [], items: string[] = [];
  const entries = Object.entries(owned).filter(([s, q]) => q > 0 && cat[s]).sort((a, b) => cat[a[0]].level - cat[b[0]].level);
  let corrupted = 0;
  for (const [s, q] of entries) {
    const c = cat[s];
    for (let i = 0; i < Math.min(q, copiesAllowed(c)); i++) {
      if (c.type === 'corrupted' && corrupted >= DECK_RULES.corruptedMax) break;
      if (c.type === 'corrupted') corrupted++;
      if ((c.type === 'fighter' || c.type === 'corrupted') && fighters.length < DECK_RULES.fightersMax) fighters.push(s);
      else if (c.type === 'item' && items.length < DECK_RULES.itemsMax) items.push(s);
    }
  }
  const gls = entries.map(([s]) => s).filter((s) => cat[s].type === 'gooflord');
  const gooflord = prefGooflord && gls.includes(prefGooflord) ? prefGooflord : gls[0] ?? Object.values(cat).find((c) => c.type === 'gooflord')?.slug ?? 'blob-bear';
  return { gooflord, fighters, items };
}

/** Average printed level of a deck's ordinary fighters (Corrupted excluded) — how strong a deck plays early on. */
export function deckStrength(fighters: string[], cat: Record<string, Card>): number {
  const lv = fighters.map((s) => cat[s]).filter((c) => c?.type === 'fighter').map((c) => c.level);
  return lv.length ? lv.reduce((a, b) => a + b, 0) / lv.length : 3;
}

/**
 * A random legal AI deck from the whole catalog. With `matchLevel` (the average fighter level of the human's deck) its
 * fighters cluster around that level, nudged by difficulty, so a new player's starter deck meets a fair opponent.
 * Without it, `difficulty` just caps the fighters' level.
 */
export function aiDeck(cards: Card[], seed: number, opts: { gooflord?: string; difficulty?: 'easy' | 'normal' | 'hard'; name?: string; id?: string; matchLevel?: number } = {}): DeckInput {
  const rs = { rng: seed >>> 0 };
  const active = cards.filter((c) => c.type === 'fighter' && !c.inactive);
  let pool: string[];
  if (opts.matchLevel != null) {
    const target = Math.max(1.5, opts.matchLevel + (opts.difficulty === 'hard' ? 1.5 : opts.difficulty === 'easy' ? -1 : 0));
    // closest levels first, with a little noise so two AI decks at the same level differ
    pool = shuffleWith(active, rs).map((c) => ({ c, d: Math.abs(c.level - target) + nextRandom(rs) * 1.5 })).sort((a, b) => a.d - b.d).slice(0, 18).map((x) => x.c.slug);
  } else {
    const cap = opts.difficulty === 'hard' ? 20 : opts.difficulty === 'easy' ? 5 : 8;
    pool = shuffleWith(active.filter((c) => c.level <= cap).map((c) => c.slug), rs).slice(0, 18);
  }
  const corr = shuffleWith(cards.filter((c) => c.type === 'corrupted' && c.level < 100 && !c.inactive).map((c) => c.slug), rs).slice(0, opts.difficulty === 'easy' ? 0 : 1);
  const items = shuffleWith(cards.filter((c) => c.type === 'item' && !c.inactive).map((c) => c.slug), rs).slice(0, DECK_RULES.itemsMax);
  const gls = cards.filter((c) => c.type === 'gooflord' && !c.inactive);
  const gl = opts.gooflord && gls.some((g) => g.slug === opts.gooflord) ? opts.gooflord : gls[Math.floor(nextRandom(rs) * gls.length)].slug;
  const glName = cards.find((c) => c.slug === gl)?.name ?? 'AI';
  return { player: { id: opts.id ?? 'ai', name: opts.name ?? `${glName} (AI)`, avatar: '🤖' }, gooflord: gl, fighters: pool.concat(corr), items, ai: true };
}

// ---------------------------------------------------------------- Deck Builder

export type DeckStyle = 'power' | 'swarm' | 'control' | 'corrupted' | 'balanced';
export interface DeckStyleDef {
  id: DeckStyle; name: string; emoji: string; blurb: string;
  /** score an owned fighter for this style (higher is better) */
  fighterScore: (c: Card) => number;
  /** item abilities, best first; items with other abilities only fill the minimum */
  items: string[];
  /** Gooflord abilities, best first */
  leaders: string[];
  corrupted: number;
}

export const DECK_STYLES: DeckStyleDef[] = [
  { id: 'power', name: 'Big Hitters', emoji: '💪', blurb: 'Your highest-level fighters and every buff you own. Win raids by being bigger.',
    fighterScore: (c) => c.level, items: ['kill', 'buff', 'buff_draw', 'd20', 'debuff_target', 'roll_bonus', 'insure', 'scry'], leaders: ['blob_bear', 'tooth_catcher', 'bog'], corrupted: 1 },
  { id: 'swarm', name: 'Swarm', emoji: '🐜', blurb: 'Lots of cheap fighters that tame together, plus items that drop more into your party.',
    fighterScore: (c) => 10 - Math.abs(c.level - 3), items: ['shuffle_draw_fighter', 'everyone_draw_fighter', 'draw_bottom', 'summon', 'buff', 'scry', 'draw_items', 'insure'], leaders: ['cluck', 'ducky', 'bog'], corrupted: 0 },
  { id: 'control', name: 'Control', emoji: '🧠', blurb: 'Solid mid-level fighters and every removal and disruption card: make them discard, stun and skip.',
    fighterScore: (c) => 10 - Math.abs(c.level - 6), items: ['kill', 'discard_enemy_fighter', 'd20_discard_fighter', 'stun', 'skip_turn', 'discard_enemy_items', 'egg', 'block_items', 'debuff_target'], leaders: ['king_plum', 'pip', 'party_dice'], corrupted: 1 },
  { id: 'corrupted', name: 'Corrupted Chaos', emoji: '☠️', blurb: 'Both Corrupted slots filled, item draw to feed Little Jimbo, and the Toxic Blobber combo if you have it.',
    fighterScore: (c) => c.level + (c.slug === 'toxic-blobber' ? 50 : 0), items: ['draw_items', 'egg', 'buff_draw', 'recover_item', 'd20_revive_corrupted', 'draw_bottom', 'scry'], leaders: ['jimbo', 'ducky', 'pip'], corrupted: 2 },
  { id: 'balanced', name: 'Balanced', emoji: '⚖️', blurb: 'A spread of levels and a bit of everything. A good first deck.',
    fighterScore: (c) => c.level, items: ['kill', 'buff', 'draw_items', 'discard_enemy_fighter', 'buff_draw', 'debuff_target', 'egg', 'shuffle_draw_fighter', 'scry'], leaders: ['tooth_catcher', 'blob_bear', 'king_plum'], corrupted: 1 },
];

/**
 * Build the best legal deck for a play style from what the player owns. Every style fills the deck as far as the
 * collection allows (DECK_RULES.fightersMax fighters, itemsMax items); the style decides which cards go in first.
 */
export function buildDeck(style: DeckStyle, owned: Record<string, number>, cat: Record<string, Card>): { gooflord: string; fighters: string[]; items: string[] } {
  const def = DECK_STYLES.find((d) => d.id === style) ?? DECK_STYLES[4];
  const have = (c: Card) => Math.min(copiesAllowed(c), owned[c.slug] ?? 0);
  const mine = Object.values(cat).filter((c) => have(c) > 0 && !c.inactive);
  const byName = (a: Card, b: Card) => a.name.localeCompare(b.name);

  // fighters: Corrupted first (capped), then ordinary fighters by score
  const fighters: string[] = [];
  const corr = mine.filter((c) => c.type === 'corrupted' && c.level < 1000)
    .sort((a, b) => (b.slug === 'corrupted-toxic-blobber' && owned['toxic-blobber'] ? 1 : 0) - (a.slug === 'corrupted-toxic-blobber' && owned['toxic-blobber'] ? 1 : 0) || b.level - a.level || byName(a, b));
  for (const c of corr) for (let k = 0; k < have(c) && fighters.length < Math.min(def.corrupted, DECK_RULES.corruptedMax); k++) fighters.push(c.slug);
  const ordinary = mine.filter((c) => c.type === 'fighter').sort((a, b) => def.fighterScore(b) - def.fighterScore(a) || byName(a, b));
  // Balanced takes one of each fighter before any extra copies (when the rules allow more than one), so it spreads across levels
  const passes = style === 'balanced' ? [1, DECK_RULES.maxCopies] : [DECK_RULES.maxCopies];
  for (const upTo of passes) for (const c of ordinary) while (fighters.length < DECK_RULES.fightersMax && fighters.filter((s) => s === c.slug).length < Math.min(upTo, have(c))) fighters.push(c.slug);

  // items: the style's abilities in priority order, then anything else to reach the maximum
  const items: string[] = [];
  const rank = (c: Card) => { const i = def.items.indexOf(c.ability ?? ''); return i < 0 ? 99 : i; };
  const allItems = mine.filter((c) => c.type === 'item').sort((a, b) => rank(a) - rank(b) || byName(a, b));
  for (const c of allItems) for (let k = 0; k < have(c) && items.length < DECK_RULES.itemsMax; k++) items.push(c.slug);

  const leaders = mine.filter((c) => c.type === 'gooflord');
  const lrank = (c: Card) => { const i = def.leaders.indexOf(c.ability ?? ''); return i < 0 ? 99 : i; };
  const gooflord = leaders.sort((a, b) => lrank(a) - lrank(b) || byName(a, b))[0]?.slug ?? autoDeck(owned, cat).gooflord;
  return { gooflord, fighters, items };
}
